// @flow

import { action, observable, runInAction } from "mobx"
import { isUndefined } from "lodash"
import OrderAdminGateway from "shared/apis/OrderAdminGateway"
import { messageError } from "shared/utils/errors"
import type {
  DetailedOrder,
  Order,
  VehicleOnMapDetails
} from "shared/types/OrdersType"

const handleError = error => {
  messageError("order-search", error)
  throw error
}

class OrderSearchStore {
  @observable orders: Array<Order> = []
  @observable loading: boolean = false
  @observable selectedOrder: Order = {}
  @observable detailedOrder: DetailedOrder = {}
  @observable vehicleOnMapDetails: VehicleOnMapDetails = {}
  @observable queryParams: Object = {}

  // Pagination
  @observable totalCount = 0
  @observable currentPage = 1
  @observable pageSize = 25
  @observable isFetchingOrders = false
  @observable hasMoreOrders = true
  @observable fromIndex = 0

  @action
  searchWithFromIndex = async ({
    orderId,
    phoneNumber,
    fromAddress,
    toAddress,
    startDate,
    endDate,
    source,
    state,
    brand,
    fleet,
    customerName,
    isFetchMore = false
  }: {
    orderId: string,
    phoneNumber: string,
    fromAddress: string,
    toAddress: string,
    startDate: Object,
    endDate: Object,
    source: Object,
    state: Object,
    brand: Object,
    fleet: Object,
    customerName: string,
    isFetchMore: boolean
  } = {}) => {
    if (this.isFetchingOrders || (!this.hasMoreOrders && isFetchMore)) return

    runInAction(() => {
      this.isFetchingOrders = true
      if (!isFetchMore) {
        this.fromIndex = 0
        this.hasMoreOrders = true
      }
    })
    try {
      if (!isFetchMore) {
        // Store to reuse when triggered by fetchMore
        this.queryParams = {
          orderId: orderId,
          phoneNumber: phoneNumber,
          fromAddress: fromAddress,
          toAddress: toAddress,
          startDate: startDate,
          endDate: endDate,
          source: source,
          state: state,
          brand: brand,
          fleet: fleet,
          customerName: customerName
        }
      } else {
        orderId = this.queryParams.orderId
        phoneNumber = this.queryParams.phoneNumber
        fromAddress = this.queryParams.fromAddress
        toAddress = this.queryParams.toAddress
        startDate = this.queryParams.startDate
        endDate = this.queryParams.endDate
        source = this.queryParams.source
        state = this.queryParams.state
        brand = this.queryParams.brand
        fleet = this.queryParams.fleet
        customerName = this.queryParams.customerName
      }
      const result = await OrderAdminGateway.orders.findOrders({
        config: {
          params: {
            orderId: orderId.length ? orderId : null,
            phoneNumber: phoneNumber.length ? phoneNumber : null,
            fromAddress: fromAddress.length ? fromAddress : null,
            toAddress: toAddress.length ? toAddress : null,
            fromDate: !isUndefined(startDate)
              ? startDate.format("YYYY-MM-DDTHH:mm")
              : null,
            toDate: !isUndefined(endDate)
              ? endDate.format("YYYY-MM-DDTHH:mm")
              : null,
            source: !isUndefined(source) ? source : null,
            state: !isUndefined(state) ? state : null,
            brand: !isUndefined(brand) ? brand : null,
            fleetId: !isUndefined(fleet) ? fleet : null,
            traveler: customerName.length ? customerName : null,
            fromIndex: this.fromIndex,
            size: this.pageSize
          }
        }
      })
      runInAction(() => {
        const newOrders = result.orders
        if (isFetchMore) {
          this.orders = [...this.orders, ...newOrders]
        } else {
          this.orders = newOrders
          this.totalCount = result.totalCount
        }
        this.fromIndex += newOrders.length
        this.hasMoreOrders = this.fromIndex < this.totalCount
        this.isFetchingOrders = false
      })
    } catch (e) {
      handleError(e)
    }
  }

  @action
  async findDetailedOrderById(orderId: string) {
    const result = await OrderAdminGateway.orders.findOneById(orderId)
    runInAction(() => {
      this.setDetailedOrder(result)
    })
  }

  @action
  async findVehicleOnMap(orderId: string, latitude: string, longitude: string) {
    function buildQueryParam(latitude: string, longitude: string) {
      return "latitude=" + latitude + "&longitude=" + longitude
    }
    this.setVehicleOnMapDetails({})
    const result = await OrderAdminGateway.orders.findVehicleOnMap(
      orderId,
      buildQueryParam(latitude, longitude)
    )
    runInAction(() => {
      this.setVehicleOnMapDetails(result)
    })
  }

  async adminCompleteOrder(orderId: string) {
    await OrderAdminGateway.orders.adminCompleteOrder(orderId)
    return this.findDetailedOrderById(orderId)
  }

  async adminCancelOrder(orderId: string) {
    await OrderAdminGateway.orders.AdminCancelOrder(orderId)
    return this.findDetailedOrderById(orderId)
  }

  async cancelOrder(orderId: string) {
    await OrderAdminGateway.orders.cancelOrder(orderId)
    return this.findDetailedOrderById(orderId)
  }

  @action
  setIsLoading(value: boolean = true) {
    this.loading = value
  }

  @action
  setOrders(orders: Array<Order>) {
    this.orders = orders
  }

  @action
  setDetailedOrder(order: DetailedOrder) {
    this.detailedOrder = order
  }

  @action
  clearDetailedOrder() {
    this.detailedOrder = {}
  }

  @action
  setSelectedOrder(order: any) {
    this.selectedOrder = order
  }

  @action
  setTotalCount(total: number) {
    this.totalCount = total
  }

  @action
  setCurrentPage(page: number) {
    this.currentPage = page
  }

  @action
  reset() {
    this.orders = []
    this.loading = false
  }

  @action
  setVehicleOnMapDetails(vehicleOnMap: VehicleOnMapDetails) {
    this.vehicleOnMapDetails = vehicleOnMap
  }
}

export default (new OrderSearchStore(): any)
